import {
  Button,
  Alert,
  PhoneInputField,
  FormField,
  TextInput,
  ToggleInput,
  SuccessNotice,
} from "@a2z/ui";

import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { toE164OrNull } from "@/lib/phone/phone";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateAdminMutation } from "../services/useUpdateAdminMutation";
import { useLocale } from "@admin/app/locale";
import { getPhoneInputLabels } from "@/shared/components/forms/phone-input-labels";

export function EditForm({
  adminId,
  defaultValues,
}: {
  adminId: string;
  defaultValues: {
    name?: string;
    email?: string;
    phone?: string;
    roles?: string[];
  };
}) {
  const t = useTranslations();
  const locale = useLocale();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [phone, setPhone] = useState(defaultValues.phone ?? "");
  const { mutateAsync: updateAdmin } = useUpdateAdminMutation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setSuccess(false);
    const formData = new FormData(event.currentTarget);
    const roles = formData.getAll("roles").map((value) => String(value));
    if (roles.length === 0) {
      setFieldErrors({ roles: t(keys.errors.required) });
      return;
    }
    const normalizedPhone =
      phone.trim().length === 0
        ? undefined
        : (toE164OrNull(phone) ?? undefined);
    if (phone.trim().length > 0 && !normalizedPhone) {
      setFieldErrors({ phone: t(keys.buyer.trackingValidationPhone) });
      return;
    }

    try {
      await updateAdmin({
        adminId,
        name: String(formData.get("name") ?? ""),
        email: String(formData.get("email") ?? ""),
        phone: normalizedPhone,
        roles,
      });
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      return;
    }

    setSuccess(true);
  };

  return (
    <form
      className="mx-auto flex w-full max-w-[640px] flex-col gap-4"
      onSubmit={handleSubmit}
    >
      {error ? (
        <Alert message={error} helperText={t(keys.errors.recovery)} />
      ) : null}
      {success ? <SuccessNotice title={t(keys.common.updateSuccess)} /> : null}
      <FormField label={t(keys.common.name)} name="name">
        <TextInput
          name="name"
          defaultValue={defaultValues.name}
          placeholder={t(keys.placeholders.name)}
        />
      </FormField>
      <div className="grid gap-4 lg:grid-cols-2">
        <FormField label={t(keys.common.email)} name="email">
          <TextInput
            name="email"
            type="email"
            defaultValue={defaultValues.email}
            placeholder={t(keys.placeholders.email)}
          />
        </FormField>
        <FormField
          label={t(keys.common.phone)}
          name="phone"
          error={fieldErrors.phone}
        >
          <PhoneInputField
            name="phone"
            value={phone}
            onChange={setPhone}
            locale={locale}
            labels={getPhoneInputLabels(t)}
            hasError={Boolean(fieldErrors.phone)}
          />
        </FormField>
      </div>
      <FormField
        label={t(keys.admin.rolesLabel)}
        name="roles"
        required
        error={fieldErrors.roles}
        helperText={t(keys.admin.rolesHelper)}
      >
        <div className="grid gap-2 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-sm">
          {[
            ADMIN_ROLES.ADMIN,
            ADMIN_ROLES.SUPER_ADMIN,
            ADMIN_ROLES.CUSTOMER_SERVICE,
            ADMIN_ROLES.SALES,
          ].map((role) => (
            <label key={role} className="flex items-center gap-3">
              <ToggleInput
                name="roles"
                defaultChecked={defaultValues.roles?.includes(role)}
                value={role}
              />
              <span className="text-[color:var(--text-secondary)]">
                {t(
                  role === ADMIN_ROLES.ADMIN
                    ? keys.admin.roleAdmin
                    : role === ADMIN_ROLES.SUPER_ADMIN
                      ? keys.admin.roleSuperAdmin
                      : role === ADMIN_ROLES.CUSTOMER_SERVICE
                        ? keys.admin.roleCustomerService
                        : keys.admin.roleSales,
                )}
              </span>
            </label>
          ))}
        </div>
      </FormField>
      <Button
        type="submit"
        variant="primary"
        className="h-11 w-full rounded-lg px-4 text-sm font-semibold transition"
      >
        {t(keys.common.update)}
      </Button>
    </form>
  );
}
